/**
 * The setup-revision slice of the UI store: which revision is open, how far it
 * is verified, and which single navigation intent is waiting on the guard.
 *
 * It owns no transport and no host bridge — the screen-change side effect is
 * injected, so the slice stays a pure state transition over plain data.
 */

import type { Option, Screen } from '../types';
import {
  canOpenSetupRevision,
  canRecordVerifiedSetup,
  InitialSetupRevisionStatus,
  nextSetupRevisionStatus,
  type NavigationIntent,
  type SetupRevisionEvent,
  type SetupRevisionStatus,
  type VerifiedSetupBaseline,
} from '../lib/setupRevision';
import { getPreviousReachableScreen, truncateWorkflowReachability } from './uiNavigationState';

export interface SetupRevisionState {
  /** Monotonic per project/document session; never rewinds. */
  setupRevisionId: number;
  setupRevisionStatus: SetupRevisionStatus;
  setupRevisionProjectKey: string | null;
  /**
   * The whole evidence of the last verification - profile, setup fingerprint,
   * revision and document - so a discard can restore exactly what was proven,
   * not merely the profile that happens to be selected now.
   */
  verifiedSetupBaseline: VerifiedSetupBaseline | null;
  /** Furthest workflow step this revision has actually committed to. */
  committedWorkflowStep: Screen | null;
  /** The one navigation request the guard has not resolved yet. */
  pendingNavigationIntent: NavigationIntent | null;
}

export const initialSetupRevisionState: SetupRevisionState = {
  setupRevisionId: 0,
  setupRevisionStatus: InitialSetupRevisionStatus,
  setupRevisionProjectKey: null,
  verifiedSetupBaseline: null,
  committedWorkflowStep: null,
  pendingNavigationIntent: null,
};

/** Cleared whenever the project session ends; the revision id deliberately is not. */
export const clearedSetupRevisionState: Omit<SetupRevisionState, 'setupRevisionId'> = {
  setupRevisionStatus: InitialSetupRevisionStatus,
  setupRevisionProjectKey: null,
  verifiedSetupBaseline: null,
  committedWorkflowStep: null,
  pendingNavigationIntent: null,
};

export interface BeginSetupRevisionRequest {
  projectKey: string;
  /** Setup screen the user returned to; defaults to the previous reachable screen. */
  boundaryScreen?: Screen;
}

export interface SetupRevisionActions {
  beginSetupRevision: (request: BeginSetupRevisionRequest) => void;
  applySetupRevisionEvent: (event: SetupRevisionEvent) => void;
  /** Freezes the setup a save was verified against, and opens the readiness check. */
  recordVerifiedSetup: (baseline: VerifiedSetupBaseline) => void;
  /** Completes a revision whose readiness check confirmed the recorded setup. */
  confirmSetupReadiness: () => void;
  beginNavigationIntent: (intent: NavigationIntent) => void;
  endNavigationIntent: (intentId: string) => void;
}

/** The navigation state a revision transition is allowed to read. */
export interface SetupRevisionNavigationContext {
  screen: Screen;
  navigationHistory: Screen[];
  selectedOption: Option;
}

type SetupRevisionSliceState = SetupRevisionState & SetupRevisionNavigationContext;

export interface SetupRevisionSliceOptions {
  /** Raised after a revision moves the user to its boundary screen. */
  onScreenChanged: (screen: Screen) => void;
}

/**
 * Events that describe a checkpoint result. Opening a revision, recording a
 * verified read-back and completing a revision are deliberately not among them:
 * each needs data or evidence the event channel cannot supply, so they have
 * their own actions and are ignored here.
 */
const eventsOwnedByDedicatedActions = new Set<SetupRevisionEvent>([
  'returnToSetup',
  'profileReadBackVerified',
  'readinessVerified',
]);

const canOpenRevision = (state: SetupRevisionSliceState, projectKey: string): boolean =>
  canOpenSetupRevision(state.setupRevisionStatus, state.setupRevisionProjectKey, projectKey);

export const createSetupRevisionActions = (
  set: (partial: Partial<SetupRevisionSliceState>) => void,
  get: () => SetupRevisionSliceState,
  { onScreenChanged }: SetupRevisionSliceOptions
): SetupRevisionActions => ({
  beginSetupRevision: ({ projectKey, boundaryScreen }) => {
    const state = get();
    if (!canOpenRevision(state, projectKey)) {
      return;
    }

    const boundary =
      boundaryScreen ?? getPreviousReachableScreen(state.navigationHistory, state.screen);
    const reachability = truncateWorkflowReachability(boundary, state.selectedOption);
    const keepsVerifiedBaseline = state.setupRevisionProjectKey === projectKey;

    set({
      ...reachability,
      setupRevisionId: state.setupRevisionId + 1,
      setupRevisionStatus: 'revisionOpen',
      setupRevisionProjectKey: projectKey,
      committedWorkflowStep: reachability.screen,
      pendingNavigationIntent: null,
      ...(keepsVerifiedBaseline ? {} : { verifiedSetupBaseline: null }),
    });

    if (reachability.screen !== state.screen) {
      onScreenChanged(reachability.screen);
    }
  },

  applySetupRevisionEvent: (event) => {
    if (eventsOwnedByDedicatedActions.has(event)) {
      return;
    }

    const { setupRevisionStatus } = get();
    const nextStatus = nextSetupRevisionStatus(setupRevisionStatus, event);
    if (!nextStatus) {
      return;
    }

    set({
      setupRevisionStatus: nextStatus,
      // A revision that needs recovery has no verified target left to trust.
      ...(nextStatus === 'recoveryRequired' ? { verifiedSetupBaseline: null } : {}),
    });
  },

  recordVerifiedSetup: (baseline) => {
    const { setupRevisionId, setupRevisionStatus } = get();
    // A checkpoint that resolved late speaks for a revision that is over: it may
    // neither record its evidence nor move the revision now in flight.
    if (baseline.revisionId !== setupRevisionId) {
      return;
    }
    if (!canRecordVerifiedSetup(setupRevisionStatus)) {
      return;
    }

    set({
      verifiedSetupBaseline: baseline,
      // A revision waiting on its profile now has what it was waiting for and
      // moves on to the readiness check; a settled session only records.
      setupRevisionStatus:
        nextSetupRevisionStatus(setupRevisionStatus, 'profileReadBackVerified') ??
        setupRevisionStatus,
    });
  },

  confirmSetupReadiness: () => {
    const nextStatus = nextSetupRevisionStatus(get().setupRevisionStatus, 'readinessVerified');
    if (!nextStatus) {
      return;
    }

    set({ setupRevisionStatus: nextStatus });
  },

  beginNavigationIntent: (intent) => {
    if (get().pendingNavigationIntent) {
      return;
    }
    set({ pendingNavigationIntent: intent });
  },

  endNavigationIntent: (intentId) => {
    const pending = get().pendingNavigationIntent;
    if (!pending || pending.id !== intentId) {
      return;
    }
    set({ pendingNavigationIntent: null });
  },
});
